import { addDays, differenceInSeconds, startOfDay } from "date-fns";
import { format as formatTz, fromZonedTime, toZonedTime } from "date-fns-tz";

export const IST_TZ = "Asia/Kolkata";

/**
 * Returns the current instant viewed as IST wall-clock.
 * Underlying timestamp is shifted so .getHours()/.getDate() return IST values
 * regardless of host timezone — used for IST-anchored display + scheduling.
 */
export function nowIst(reference: Date = new Date()): Date {
  return toZonedTime(reference, IST_TZ);
}

export function toIst(d: Date): Date {
  return toZonedTime(d, IST_TZ);
}

/** Convert IST wall-clock date to a true UTC instant. */
export function istWallClockToUtc(d: Date): Date {
  return fromZonedTime(d, IST_TZ);
}

export function startOfIstDay(d: Date = new Date()): Date {
  const z = toZonedTime(d, IST_TZ);
  const sod = startOfDay(z);
  return fromZonedTime(sod, IST_TZ);
}

export function addIstDays(d: Date, n: number): Date {
  const z = toZonedTime(d, IST_TZ);
  const a = addDays(z, n);
  return fromZonedTime(a, IST_TZ);
}

export function formatIst(d: Date, pattern: string): string {
  return formatTz(d, pattern, { timeZone: IST_TZ });
}

export function diffSeconds(a: Date, b: Date): number {
  return differenceInSeconds(a, b);
}

export function clampPositiveSeconds(n: number): number {
  return n > 0 ? n : 0;
}

/**
 * Format a 24h "HH:mm" string as 12-hour with meridian (e.g. "13:30" -> "1:30 PM").
 * Returns empty string for invalid input.
 */
/**
 * Construct a UTC Date that, viewed in IST, falls on the same IST calendar
 * day as `anchorIst` at the given "HH:mm" wall-clock. Returns null for
 * malformed input or out-of-range values.
 *
 * Used by the prayer engine to apply admin Azaan/Jama'ah overrides on top of
 * the computed schedule. Cross-TZ safe: works whether the host is UTC, IST,
 * or anywhere else.
 */
export function parseHHmmInIst(hhmm: string, anchorIst: Date): Date | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  const year = anchorIst.getFullYear();
  const month = anchorIst.getMonth();
  const day = anchorIst.getDate();
  // Host-local construction; fromZonedTime then re-interprets as IST.
  const local = new Date(year, month, day, hh, mm, 0, 0);
  return fromZonedTime(local, IST_TZ);
}

export function formatHHmmTo12h(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return "";
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";
  if (hours > 23 || minutes > 59) return "";
  const meridian = hours >= 12 ? "PM" : "AM";
  const display = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${display}:${minutes.toString().padStart(2, "0")} ${meridian}`;
}
